import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Alert,
    Divider,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Tooltip
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { makeApiRequest } from '../../../utils/api';
import { toast, ToastContainer } from 'react-toastify';

const COMPONENT_MAP = {
    'DOMAIN TECHNICAL CONCEPT': ['written', 'viva'],
    'DRAFTING': ['written', 'viva', 'project_presentation'],
    'MODELING': ['written', 'viva', 'project_presentation'],
    'ATTENDANCE': ['attendance'],
    'PROJECT (DRAFTING + MODELING)': ['project_understanding', 'explanation_technical', 'tools_skill'],
};

const maxMarksMap = {
    'domain technical concept': { written: 5, viva: 5 },
    'drafting': { written: 3, viva: 3, project_presentation: 4 },
    'modeling': { written: 3, viva: 3, project_presentation: 4 },
    'attendance': { attendance: 10 },
    'project (drafting + modeling)': { project_understanding: 3, explanation_technical: 3, tools_skill: 4 }
};

const getMaxMark = (field, assessmentName) => {
    const key = (assessmentName || '').toLowerCase().trim();
    if (maxMarksMap[key]?.[field] !== undefined) {
        return maxMarksMap[key][field];
    }
    const fallback = {
        written: 5,
        viva: 5,
        project_presentation: 3,
        project_understanding: 3,
        explanation_technical: 3,
        tools_skill: 4,
        attendance: 10
    };
    return fallback[field] || 10;
};

const toSentenceCase = (str) => {
    return str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

export default function StudentMarksEdit() {
    const location = useLocation();
    const navigate = useNavigate();
    const token = localStorage.getItem('authToken');
    
    const [studentId, setStudentId] = useState(location.state?.studentId || '');
    const [loading, setLoading] = useState(false);
    const [studentData, setStudentData] = useState(null);
    const [editedMarks, setEditedMarks] = useState({}); // { [assessment_id]: { [field]: value } }
    const [initialMarks, setInitialMarks] = useState({}); // Store initial copy for change comparison
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (studentId) {
            fetchStudentMarks();
        }
    }, [studentId]);

    const fetchStudentMarks = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await makeApiRequest('post', '/api/getStudentMarks', {
                token,
                id: Number(studentId)
            });
            
            if (response && response.data && response.data.length > 0) {
                const sData = response.data[0];
                setStudentData(sData);

                // Build initial marks state
                const marksObj = {};
                sData.assessments.forEach(item => {
                    const fields = COMPONENT_MAP[item.generic_name] || [];
                    marksObj[item.assessment_id] = {};
                    fields.forEach(field => {
                        const val = item.marks[field];
                        marksObj[item.assessment_id][field] = val !== null ? val : '';
                    });
                });

                setEditedMarks(JSON.parse(JSON.stringify(marksObj)));
                setInitialMarks(JSON.parse(JSON.stringify(marksObj)));
            } else {
                setError('No student marks data found.');
            }
        } catch (err) {
            console.error(err);
            setError('Failed to fetch student details. Please verify the student ID.');
        } finally {
            setLoading(false);
        }
    };

    const calculateAssessmentTotal = (assessmentId) => {
        const marks = editedMarks[assessmentId] || {};
        return Object.values(marks).reduce((sum, val) => sum + (val === '' || val === null || val === undefined ? 0 : Number(val)), 0);
    };

    const isDirty = studentData
        ? studentData.assessments.some(item => {
            const fields = COMPONENT_MAP[item.generic_name] || [];
            return fields.some(field => editedMarks[item.assessment_id]?.[field] !== initialMarks[item.assessment_id]?.[field]);
        })
        : false;

    const handleMarkChange = (assessmentId, field, value) => {
        setEditedMarks(prev => ({
            ...prev,
            [assessmentId]: {
                ...prev[assessmentId],
                [field]: value === '' ? '' : Number(value)
            }
        }));
    };

    const isCertificateGenerated = !!studentData?.certificate_generated;

    const handleSubmitRequest = async () => {
        if (isCertificateGenerated) {
            toast.error('Marks cannot be edited because the certificate has already been generated.');
            return;
        }
        setSubmitting(true);
        try {
            // Determine which assessments actually changed
            const changes = [];
            const oldDataList = [];

            studentData.assessments.forEach(item => {
                const aId = item.assessment_id;
                const fields = COMPONENT_MAP[item.generic_name] || [];
                let hasChanged = false;
                
                const proposedMarks = {};
                const currentMarks = {};

                fields.forEach(field => {
                    const newVal = editedMarks[aId][field];
                    const oldVal = initialMarks[aId][field];
                    
                    proposedMarks[field] = newVal === '' ? null : newVal;
                    currentMarks[field] = oldVal === '' ? null : oldVal;

                    if (newVal !== oldVal) {
                        hasChanged = true;
                    }
                });

                if (hasChanged) {
                    changes.push({
                        assessment_id: aId,
                        assessment_name: item.generic_name,
                        marks: proposedMarks
                    });

                    oldDataList.push({
                        assessment_id: aId,
                        assessment_name: item.generic_name,
                        marks: currentMarks
                    });
                }
            });

            if (changes.length === 0) {
                toast.warning('No changes were detected to submit.');
                setSubmitting(false);
                return;
            }

            // Create edit request
            await makeApiRequest('post', '/api/edit-requests/save', {
                token,
                module: 'marks',
                reference_id: Number(studentId),
                old_data: oldDataList,
                new_data: changes
            });

            toast.success('Edit request submitted successfully! Pending admin approval.');
            setTimeout(() => {
                navigate('/assessment');
            }, 2000);

        } catch (err) {
            console.error(err);
            toast.error('Failed to submit edit request.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: 4, backgroundColor: '#F1F4F9', minHeight: '100vh' }}>
            <ToastContainer position="top-center" autoClose={2000} theme="colored" />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Button 
                    startIcon={<ArrowBackIcon />} 
                    onClick={() => navigate(-1)} 
                    variant="outlined"
                    sx={{ backgroundColor: 'white' }}
                >
                    Back
                </Button>
                <Typography variant="h4" sx={{ color: '#673ab7', fontWeight: 600 }}>
                    Edit Student Assessment Marks
                </Typography>
                <Box width={100} /> {/* Spacer */}
            </Box>

            {!location.state?.studentId && !studentData && (
                <Card sx={{ maxWidth: 500, mx: 'auto', mt: 4, p: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Find Student by ID</Typography>
                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            <TextField
                                label="Student ID"
                                variant="outlined"
                                size="small"
                                value={studentId}
                                onChange={(e) => setStudentId(e.target.value)}
                                fullWidth
                            />
                            <Button variant="contained" onClick={fetchStudentMarks}>Search</Button>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                    <CircularProgress size={50} />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
                    {error}
                </Alert>
            )}

            {studentData && (
                <Grid container spacing={3}>
                    {/* Student Info Card */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, borderRadius: '8px' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={3}>
                                    <Typography variant="caption" color="textSecondary">Student Name</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>{studentData.student_name}</Typography>
                                        {isCertificateGenerated && (
                                            <Tooltip title="Certificate generated, can't able to edit">
                                                <Chip
                                                    icon={<WorkspacePremiumIcon sx={{ fontSize: '16px !important' }} />}
                                                    label="Certificate Generated"
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#e0f7ed',
                                                        color: '#00a36f',
                                                        fontWeight: 600,
                                                        fontSize: '0.7rem',
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Typography variant="caption" color="textSecondary">User ID</Typography>
                                    <Typography variant="h6" color="primary">{studentData.userid}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Typography variant="caption" color="textSecondary">Course Name</Typography>
                                    <Typography variant="h6">{studentData.course_name}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Typography variant="caption" color="textSecondary">Internal Database ID</Typography>
                                    <Typography variant="h6">{studentData.student_id}</Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Marks Edit Form */}
                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: '8px', p: 1 }}>
                            <CardContent>
                                <Typography variant="h5" sx={{ mb: 3, fontWeight: 500, color: '#333' }}>
                                    Edit Assessment Marks
                                </Typography>
                                {isCertificateGenerated && (
                                    <Alert severity="warning" sx={{ mb: 3 }}>
                                        Marks cannot be edited because the certificate has already been generated for this student.
                                    </Alert>
                                )}
                                <Divider sx={{ mb: 3 }} />
                                
                                {studentData.assessments.map(item => {
                                    const fields = COMPONENT_MAP[item.generic_name] || [];
                                    if (fields.length === 0) return null;

                                    return (
                                        <Box key={item.assessment_id} sx={{ mb: 4, p: 2, border: '1px solid #E0E0E0', borderRadius: '6px' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                                <Typography variant="h6" sx={{ color: '#673ab7', fontWeight: 600 }}>
                                                    {item.generic_name}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        bgcolor: '#e0f7ed',
                                                        color: '#00a36f',
                                                        borderRadius: '12px',
                                                        px: 1.5,
                                                        py: 0.25,
                                                        fontWeight: 600,
                                                        fontSize: '0.8rem',
                                                    }}
                                                >
                                                    Total Marks: {calculateAssessmentTotal(item.assessment_id)}
                                                </Box>
                                            </Box>
                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                                {item.specific_name} (Status: {item.status})
                                            </Typography>
                                            <Grid container spacing={3}>
                                                {fields.map(field => {
                                                    const maxMark = getMaxMark(field, item.generic_name);
                                                    const currentVal = editedMarks[item.assessment_id]?.[field];
                                                    const isEntered = initialMarks[item.assessment_id]?.[field] !== '' && initialMarks[item.assessment_id]?.[field] !== undefined;

                                                    // Generate options with 0.5 increments
                                                    const options = [];
                                                    for (let val = 0; val <= maxMark; val += 1) {
                                                        options.push(val);
                                                    }

                                                    return (
                                                        <Grid item xs={12} sm={4} key={field}>
                                                            <FormControl fullWidth size="small" disabled={!isEntered || isCertificateGenerated}>
                                                                <InputLabel>{toSentenceCase(field)} (Max: {maxMark})</InputLabel>
                                                                <Select
                                                                    value={currentVal !== undefined ? currentVal : ''}
                                                                    label={`${toSentenceCase(field)} (Max: ${maxMark})`}
                                                                    onChange={(e) => handleMarkChange(item.assessment_id, field, e.target.value)}
                                                                >
                                                                    <MenuItem value=""><em>Select</em></MenuItem>
                                                                    {options.map(val => (
                                                                        <MenuItem key={val} value={val}>{val}</MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        </Box>
                                    );
                                })}

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                                    <Button 
                                        variant="outlined" 
                                        onClick={() => navigate('/assessment')}
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                        onClick={handleSubmitRequest}
                                        disabled={submitting || !isDirty || isCertificateGenerated}
                                        sx={{ backgroundColor: '#673ab7', '&:hover': { backgroundColor: '#5e35b1' } }}
                                    >
                                        {submitting ? 'Submitting...' : 'Submit Edit Request'}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
}
